import { useState, useCallback, useMemo } from "react";
import { useAuth } from "../store";
import { RoleTypes, CapabilityType } from "../../types";
import { capabilityToRole } from "../constants/capabilities";

/**
 * Hook to manage active role for users with multiple capabilities
 *
 * When a user has advance_capabilities, they can switch between different
 * role contexts to perform actions as that role.
 *
 * @returns {Object} Active role state and utilities
 */
export const useActiveRole = () => {
  const user = useAuth((state) => state.user);
  const getCapabilities = useAuth((state) => state.getCapabilities);

  // Default active role is the user's primary role
  const [activeRole, setActiveRole] = useState<RoleTypes>(user.role);

  const capabilities = getCapabilities();

  /**
   * Get all available roles from capabilities
   */
  const availableRoles = useMemo((): RoleTypes[] => {
    const roles: Set<RoleTypes> = new Set([user.role]);

    if (user.advanceCapabilities && capabilities.length > 0) {
      capabilities.forEach((capability) => {
        const role = capabilityToRole[capability];
        if (role) {
          roles.add(role);
        }
      });
    }

    return Array.from(roles);
  }, [user.role, user.advanceCapabilities, capabilities]);

  /**
   * Check if user has multiple roles
   */
  const hasMultipleRoles = useMemo(() => {
    return availableRoles.length > 1;
  }, [availableRoles]);

  /**
   * Check if a specific role is available
   */
  const hasRole = useCallback(
    (role: RoleTypes): boolean => {
      return availableRoles.includes(role);
    },
    [availableRoles],
  );

  /**
   * Check if user can act as a specific capability
   */
  const canActAs = useCallback(
    (capability: CapabilityType): boolean => {
      if (!user.advanceCapabilities) return false;
      return capabilities.includes(capability);
    },
    [user.advanceCapabilities, capabilities],
  );

  /**
   * Change the active role
   */
  const changeRole = useCallback(
    (newRole: RoleTypes) => {
      if (availableRoles.includes(newRole)) {
        setActiveRole(newRole);
      }
    },
    [availableRoles],
  );

  /**
   * Reset to primary role
   */
  const resetToPrimaryRole = useCallback(() => {
    setActiveRole(user.role);
  }, [user.role]);

  /**
   * Get the effective role for API calls and UI rendering
   * This returns the active role if user has advance capabilities,
   * otherwise returns the primary role
   */
  const effectiveRole = useMemo((): RoleTypes => {
    if (user.advanceCapabilities && availableRoles.includes(activeRole)) {
      return activeRole;
    }
    return user.role;
  }, [user.advanceCapabilities, availableRoles, activeRole, user.role]);

  /**
   * Check if currently acting as a different role than primary
   */
  const isActingAsDifferentRole = useMemo(() => {
    return effectiveRole !== user.role;
  }, [effectiveRole, user.role]);

  return {
    // State
    activeRole,
    effectiveRole,
    primaryRole: user.role,
    availableRoles,
    capabilities,

    // Flags
    hasMultipleRoles,
    hasAdvanceCapabilities: user.advanceCapabilities,
    isActingAsDifferentRole,

    // Methods
    changeRole,
    resetToPrimaryRole,
    hasRole,
    canActAs,
  };
};

export default useActiveRole;
